import Vue from 'vue'
import {SubstanceApi} from "@core/api";
import {SubstanceListRenderMode, SubstanceLists} from "@/libs/substance";

export default class SubstanceList {

    id;
    key;
    title;
    question = null;

    __selectableCount = 0;

    substancesLoaded = false;
    substancesInited = false;
    _allowNewSubstances = false;
    substances = Vue.observable([]);
    substanceMap = {};
    substancePromise;
    legislations = [];
    referencingLists = [];
    references = [];
    visibility = null;
    concentrationRanges = [];
    renderMode = SubstanceListRenderMode.DEFAULT;

    constructor(data) {
        this.id = data.id;
        this.key = data.key;
        this.title = data.title;
        this.legislations = data.legislations;
        this.referencingLists = data.referencingLists;
        this.references = Array.isArray(data.references) ? data.references : [];
        this.visibility = data.visibility || null;
        this.concentrationRanges = data.concentrationRange;
        this._allowNewSubstances = data.allowNewSubstances;
        this.renderMode = data.presentation?.default?.renderMode || SubstanceListRenderMode.DEFAULT;

        if (data.question) {
            this.question = data.question;
        }

        // this.loadSubstances();
    }

    getId() {
        return this.id;
    }

    hasQuestion() {
        return null !== this.question;
    }

    /**
     * @param substance
     * @returns {boolean}
     * @deprecated
     */
    hasSubstance(substance) {
        return this.substanceMap.hasOwnProperty(substance.id);
    }

    getSelectableCount() {
        return this.__selectableCount;
    }

    /**
     * Indicates if substance is from external substance list
     * @deprecated
     */
    isExternal(substance) {
        if (this.hasSubstance(substance)) {
            return true === this.substanceMap[substance.id].external;
        }

        return false;
    }

    isIncluded(substance) {
        return this.extractIncluded([substance])
            .then(included => {
                return included.length > 0;
            });
    }

    extractIncluded(substances) {
        return new Promise(resolve => {
            this.loadSubstances().then(() => {
                resolve(substances.filter(s => this.substanceMap.hasOwnProperty(s.id)));
            });
        });
    }

    extractSelectableIncluded(substances) {
        return new Promise(resolve => {
            this.loadSubstances().then(() => {
                resolve(substances.filter(s => {
                    if (!this.substanceMap.hasOwnProperty(s.id)) {
                        return false;
                    }

                    return this.substanceMap[s.id].selectable === true;
                }));
            });
        });
    }

    allowNewSubstances() {
        return this._allowNewSubstances;
    }

    includeSubstance(substance, parent = null) {
        if (!this.allowNewSubstances()) {
            return new Promise(() => {
                throw new Error('It is not allowed to add substances')
            });
        }

        return this.extractIncluded([substance]).then(result => {
            if (result.length === 0) {
                //  substance is not included into a list
                return SubstanceApi
                    .linkSubstance(this.id, substance.id, parent ? parent.id : null)
                    .then(() => {
                        if (parent) {
                            parent.children.push(substance);
                        }
                        if (!this.hasSubstance(substance)) {
                            this.substanceMap[substance.id] = substance;
                            if (!parent) {
                                this.substances.push(substance);
                            }
                        }
                    });
            } else {
                if (!this.hasSubstance(substance)) {
                    this.substanceMap[substance.id] = substance;
                    if (!parent) {
                        this.substances.push(substance);
                    }
                }
            }
        });
    }

    includedIntoReferencedList(substance, referencedListId) {
        const item = this.substances.find(s => s.id === referencedListId && s.type === 'list-reference');
        if (item) {
            item.children.push(substance);
        }
    }

    loadSubstances() {
        if (this.substancesLoaded) {
            return this.substancePromise;
        }

        this.substancesLoaded = true;
        const addToMapRecursively = (substances) => {
            substances.map((substance) => {
                if (substance.selectable && !this.substanceMap.hasOwnProperty(substance.id)) {
                    this.__selectableCount++;
                }

                if (substance.type !== 'list-reference') {
                    this.substanceMap[substance.id] = substance;
                }
                if (substance.children && substance.children.length) {
                    addToMapRecursively(substance.children);
                }
            });
        }
        return this.substancePromise = SubstanceApi
            .listSubstances(this.id)
            .then(r => {
                this.substances = r.data;
                addToMapRecursively(this.substances);
                this.substancesInited = true;
                return this.substances;
            });
    }

    getSubstanceMap() {
        return this.loadSubstances().then(() => {
            return {...this.substanceMap};
        });
    }

    getSubstances() {
        if (this.substancesLoaded) {
            if (this.substancesInited) {
                return new Promise((resolve) => resolve(this.substances));
            }
            return this.substancePromise;
        }
        return this.loadSubstances();
    }

    hasIntersectionWithLegislations(legislations) {
        for (let i = 0; i < this.legislations.length; ++i) {
            if (legislations.includes(this.legislations[i].key) || legislations.includes(this.legislations[i].id)) {
                return true;
            }
        }
        return false;
    }

    hasReferencingLists() {
        return this.referencingLists.length > 0;
    }

    getReferencingLists() {
        return this.referencingLists;
    }

    getVisibilityRule() {
        return this.visibility;
    }

    getReferences() {
        return this.references;
    }

    getReferencedListIds() {
        return this.references.map(reference => reference.listId);
    }

    getSubstanceExemptions(substance) {
        return new Promise((resolve, reject) => {
            this.getSubstanceMap().then(map => {
                if (map[substance.id]) {
                    resolve(map[substance.id].exemptions ?? []);
                } else {
                    resolve([]);
                }
            }).catch(reject);
        });
    }

    getLegislations() {
        return this.legislations;
    }

    toDTO() {
        return {
            id: this.id,
            key: this.key,
            title: this.title,
            renderMode: this.renderMode,
        };
    }
};
